import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useWireValue } from '@forminator/react-wire'
import ContentEditable from '@/components/contenteditable'
import { NUMBER_DISPLAY_TYPE, TAB_TYPE_DEED } from '@/constants'
import * as actions from '@actions'
import * as store from '@store'
import useNavigate from '@hook/useNavigate'
import { Link, useParams } from 'react-router-dom'
import { MdFileDownload } from 'react-icons/md'
import { createSyntheticDownload } from '@/utils'
import { toast } from 'react-toastify'
import { SelectRunsheetDialog } from '@components/collection/SelectRunsheetDialog'
import { UpsertRunsheet } from '@components/tract/UpsertRunsheet'
import { UpsertTract } from '../UpsertTract'
import { useWireState } from '@forminator/react-wire'
import useAbortableFetch from '@/hook/useAbortableFetch'

const BASE_OWNERSHIP = {
    tract: {},
    title: {
        surface: {
            owners: [],
            totals: {},
        },
        mineral: {
            owners: [],
            totals: {},
        },
    },
    lease: {
        working: [],
        overriding: [],
    },
}

export const TractOwnership = () => {
    
    const [ownership, setOwnership] = useState(BASE_OWNERSHIP)
    const { id } = useParams()
    const [exportLoading, setExportLoading] = useState(false)
    const [showAdd, showHideAdd] = useState(false)
    const allRunsheets = useWireValue(store.runsheets)
    const [selectRunsheetDialogOpen, setSelectRunsheetDialogOpen] = useState(false)
    const [createRunsheetDialogOpen, setCreateRunsheetDialogOpen] = useState(false)
    const [numberDisplayType, setNumberDisplayType] = useWireState(store.numberDisplayType)
    const navigate = useNavigate()
    
    const hasData = useMemo(() => ownership.title.surface.owners.length
        || ownership.title.mineral.owners.length
        || ownership.lease.working.length
        || ownership.lease.overriding.length, [ownership])
    
    const updateAddress = async (partyId, value) => {
        await actions.upsertPartyAddress(partyId, value)
        await fetchOwnership(id)
        
    }
    
    const exportTract = async () => {
        setExportLoading(true)
        const res = await actions.exportTract(id, {
            formatType: numberDisplayType,
        })
        
        if (res)
            createSyntheticDownload(res.data, res.headers['content-type'], res.headers['content-name'])
        else
            toast.error('Failed to export Tract List, please try again.')
        
        setExportLoading(false)
    }
    
    const fetchOwnership = async id => {
        const ownershipData = await actions.findOwnershipFromTract(id)
        
        setOwnership(ownershipData)
    }
    
    const navigateRunsheet = () => {
        if (ownership.title.runsheet) {
            
            const runsheetId = ownership.title.runsheet.id
            const runsheetTabId = ownership.title.runsheet.runsheetTabId
            
            const search = runsheetTabId ? `?activeTab=${runsheetTabId}` : ''
            
            navigate(`/runsheet/${runsheetId}${search}`)
        } else {
            setSelectRunsheetDialogOpen(true)
        }
    }
    
    const handleToggle = () => {
        setNumberDisplayType(numberDisplayType === NUMBER_DISPLAY_TYPE.PERCENT
            ? NUMBER_DISPLAY_TYPE.DECIMAL
            : NUMBER_DISPLAY_TYPE.PERCENT)
    }
    
    const getNumberFormatted = value => {
        return numberDisplayType === NUMBER_DISPLAY_TYPE.PERCENT
            ? (value * 100).toFixed(6) + '%'
            : value.toFixed(8)
    }
    
    useAbortableFetch(() => {
        return fetchOwnership(id)
    }, [id])
    
    return (
        <>
            <div className="flex flex-col gap-4 p-4">
                <table className="no-hover shadow-lg rounded-lg">
                    <thead>
                        <tr>
                            <th>Tract Name</th>
                            <th>Gross Acreage</th>
                            <th>Assigned To</th>
                            <th>Status</th>
                            <th>Status Date</th>
                            <th>Certification Date</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{ownership.tract.name}</td>
                            <td>{ownership.tract.grossAcreage?.toFixed(6)}</td>
                            <td>{ownership.title.runsheet?.assignedTo}</td>
                            <td>{ownership.title.runsheet?.status}</td>
                            <td>{ownership.title.runsheet?.statusDate
                                && dayjs(ownership.title.runsheet.statusDate).format('MM/DD/YYYY')}</td>
                            <td>{ownership.title.runsheet?.certDate
                                && dayjs(ownership.title.runsheet.certDate).format('MM/DD/YYYY')}</td>
                            <td>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => showHideAdd(true)}>
                                    Edit
                                </button>
                            </td>
                        </tr>
                        <tr>
                            <th colSpan="7">Tract Description</th>
                        </tr>
                        <tr>
                            <td className="border-0" colSpan="7">{ownership.tract.description}&nbsp;</td>
                        </tr>
                    </tbody>
                </table>
                <div className="flex items-center gap-2">
                    Ownership Information:
                    <span className="tooltip" data-tip="To assign you must do it from the Runsheet screen">
                        <button
                            disabled={true}
                            className="btn btn-primary btn-sm"
                            onClick={() => navigateRunsheet()}>
                        Runsheet
                        </button>
                    </span>
                    <Link
                        className="btn btn-primary btn-sm"
                        to={`/runsheet/${ownership.title.runsheet?.id}/title-breakdown?activeTab=${
                            ownership.title.runsheet?.runsheetTabId || '0'
                        }`}
                        disabled={!ownership.title.runsheet?.id}>
                        Title Chain
                    </Link>
                    <button className="btn btn-primary btn-sm" onClick={exportTract}
                        disabled={exportLoading || !hasData}>
                        <MdFileDownload />Export
                    </button>
                    {!!hasData && <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleToggle()}>
                        {numberDisplayType === NUMBER_DISPLAY_TYPE.PERCENT ? 'Switch to Decimal' : 'Switch to Percent'}
                    </button>}
                </div>
                
                {!!ownership.title.surface.owners.length && <div className="max-h-[280px] overflow-auto
                    shadow-lg rounded-lg">
                    <table className="table table-sm table-nowrap card-table table-pin-rows">
                        <thead>
                            <tr>
                                <th className="w-[300px]">Surface Owners</th>
                                <th className="w-40">Surface</th>
                                <th className="w-40">Net Acreage</th>
                                <th>Address</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ownership.title.surface.owners.map(p => (
                                <tr key={p.id}>
                                    <td>{p.name}</td>
                                    <td>{getNumberFormatted(p.surface)}</td>
                                    <td>{p.netAcreage.toFixed(6)}</td>
                                    <td>
                                        <ContentEditable
                                            type="textarea"
                                            grow="sm"
                                            onEditEnd={value => updateAddress(p.id, value)}
                                            value={p.address} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>Total</td>
                                <td>
                                    {getNumberFormatted(ownership.title.surface.totals.surface)}
                                </td>
                                <td colSpan={2}>
                                    {ownership.title.surface.totals.netAcreage.toFixed(6)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>}
                
                {!!ownership.title.mineral.owners.length && <div className="max-h-[280px] overflow-auto
                    shadow-lg rounded-lg">
                    <table className="table table-sm table-nowrap card-table table-pin-rows">
                        <thead>
                            <tr>
                                <th className="w-[300px]">Mineral Owners</th>
                                <th className="w-28">Executive</th>
                                <th className="w-28">Bonus</th>
                                <th className="w-28">Delay</th>
                                <th className="w-28">Royalty</th>
                                <th className="w-28"
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content="Net Mineral Acres">
                                    NMA
                                </th>
                                <th className="w-28"
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content="Net Royalty Acres">
                                    NRA
                                </th>
                                <th>Address</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ownership.title.mineral.owners.map(p => (
                                <tr key={p.id}>
                                    <td>{p.name}</td>
                                    <td>{getNumberFormatted(p.executive)}</td>
                                    <td>{getNumberFormatted(p.bonus)}</td>
                                    <td>{getNumberFormatted(p.delay)}</td>
                                    <td>{getNumberFormatted(p.royalty)}</td>
                                    <td>{p.nma.toFixed(6)}</td>
                                    <td>{p.nra.toFixed(6)}</td>
                                    <td>
                                        <ContentEditable
                                            type="textarea"
                                            grow="sm"
                                            onEditEnd={value => updateAddress(p.id, value)}
                                            value={p.address} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>Total</td>
                                <td>{getNumberFormatted(ownership.title.mineral.totals.executive)}</td>
                                <td>{getNumberFormatted(ownership.title.mineral.totals.bonus)}</td>
                                <td>{getNumberFormatted(ownership.title.mineral.totals.delay)}</td>
                                <td>{getNumberFormatted(ownership.title.mineral.totals.royalty)}</td>
                                <td>{ownership.title.mineral.totals.nma.toFixed(6)}</td>
                                <td>{ownership.title.mineral.totals.nra.toFixed(6)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>}
                
                {!!ownership.lease.working.length && <div className="max-h-[280px] overflow-auto
                    shadow-lg rounded-lg">
                    <table className="table table-sm table-nowrap card-table table-pin-rows">
                        <thead>
                            <tr>
                                <th className="w-[300px]">Working Interest Owners</th>
                                <th className="w-28">Working Interest</th>
                                <th className="w-28">NRI</th>
                                <th className="w-28">NRI/WI</th>
                                <th>Address</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ownership.lease.working.map(p => (
                                <tr key={p.id}>
                                    <td>{p.name}</td>
                                    <td>{getNumberFormatted(p.working)}</td>
                                    <td>{getNumberFormatted(p.royalty)}</td>
                                    <td>{getNumberFormatted(p.royaltyByWorking)}</td>
                                    <td>
                                        <ContentEditable
                                            type="textarea"
                                            grow="sm"
                                            onEditEnd={value => updateAddress(p.id, value)}
                                            value={p.address} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>}
                
                {!!ownership.lease.overriding.length && <div className="max-h-[280px] overflow-auto
                    shadow-lg rounded-lg">
                    <table className="table table-sm table-nowrap card-table table-pin-rows">
                        <thead>
                            <tr>
                                <th className="w-[300px]">Overriding Royalty Interest Owners</th>
                                <th className="w-28">ORRI</th>
                                <th>Address</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ownership.lease.overriding.map(p => (
                                <tr key={p.id}>
                                    <td>{p.name}</td>
                                    <td>{p.royalty === null ? '-' : getNumberFormatted(p.royalty)}</td>
                                    <td>
                                        <ContentEditable
                                            type="textarea"
                                            grow="sm"
                                            onEditEnd={value => updateAddress(p.id, value)}
                                            value={p.address} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>}
            
            </div>
            {showAdd && (
                <UpsertTract
                    id="create-tract"
                    showAdd={showAdd}
                    showHideAdd={showHideAdd}
                    editTract={ownership.tract}
                    onSuccess={() => fetchOwnership(id)} />
            )}
            <SelectRunsheetDialog
                runsheets={allRunsheets}
                open={selectRunsheetDialogOpen}
                setOpen={setSelectRunsheetDialogOpen}
                setCreateRunsheetDialogOpen={setCreateRunsheetDialogOpen}
                confirmButtonText="Assign"
                showAssign={true}
                showTabs={true}
                onSelectRunsheet={async runsheetOrTab => {
                    if (runsheetOrTab) {
                        
                        const runsheetId = runsheetOrTab.isTab ? runsheetOrTab.runsheetId : runsheetOrTab.id
                        const runsheetTabId = runsheetOrTab.isTab ? runsheetOrTab.id : null
                        const ownershipType = runsheetOrTab.isTab ? runsheetOrTab.type : TAB_TYPE_DEED
                        
                        await actions.assignTractToRunsheet(id, {
                            runsheetId,
                            runsheetTabId,
                            ownershipType,
                        })
                        
                        await fetchOwnership(id)
                    }
                }} />
            
            <UpsertRunsheet
                showAdd={createRunsheetDialogOpen}
                showHideAdd={setCreateRunsheetDialogOpen}
                redirect={false}
                runsheetSaved={async runsheet => {
                    if (runsheet) {
                        allRunsheets.unshift(runsheet)
                        
                        await actions.assignTractToRunsheet(id, {
                            runsheetId: runsheet.id,
                            runsheetTabId: null,
                            TAB_TYPE_DEED,
                        })
                        
                        await fetchOwnership(id)
                    }
                    
                    setCreateRunsheetDialogOpen(false)
                }} />
        </>
    )
}
